import { ActionButton, RyeModalContent } from "../rye/RyeModal";
import { buildClasses } from "../utils/buildClasses";
import { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { callFirebaseFunction } from "../firebaseio/firebaseFunctions";
import { lineWobble } from "ldrs";
import { RyeIcon } from "../rye/RyeIcon";

lineWobble.register();

enum ReauthenticateFlowState {
  START,
  PLAID_LINK_OPEN,
  SYNCING,
  END,
}

export function ReauthenticateModal(props: { plaidItemId: string }) {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [needsLinkToken, setNeedsLinkToken] = useState<boolean>(true);
  const [flowState, setFlowState] = useState(ReauthenticateFlowState.START);

  // useCallback gets a stable function pointer so it can be used
  // as a dependency in useEffect
  const getLinkToken = useCallback(async () => {
    setNeedsLinkToken(false);
    try {
      const result = await callFirebaseFunction("GetPlaidLinkUpdateToken", {
        plaid_item_id: props.plaidItemId,
        account_selection_enabled: false,
      });

      if (!result.data.success || !result.data.linkToken) {
        console.log("Failed to get link token: ", result.data.error_msg);
        setNeedsLinkToken(true);
        return;
      }
      setNeedsLinkToken(false);
      setLinkToken(result.data.linkToken.link_token);
    } catch (err) {
      console.error("Caught error getting link token:", err);
      setNeedsLinkToken(true);
    }
  }, [props.plaidItemId]);

  const plaidLink = usePlaidLink({
    onSuccess: async () => {
      setFlowState(ReauthenticateFlowState.SYNCING);
      await callFirebaseFunction("SyncTransactions", {});
      await callFirebaseFunction("SyncAccounts", {});
      setFlowState(ReauthenticateFlowState.END);
    },
    onExit: () => {
      setFlowState(ReauthenticateFlowState.START);
    },
    token: linkToken,
  });

  useEffect(() => {
    if (needsLinkToken) {
      getLinkToken();
    }
  }, [needsLinkToken, getLinkToken]);

  useEffect(() => {
    if (
      flowState === ReauthenticateFlowState.PLAID_LINK_OPEN &&
      linkToken !== null &&
      plaidLink.ready
    ) {
      plaidLink.open();
    }
  }, [flowState, linkToken, plaidLink]);

  return (
    <RyeModalContent
      title="Reauthenticate account"
      content={
        <div className={buildClasses("mb-4", "max-w-md")}>
          {modalContent(flowState)}
        </div>
      }
      actionButtons={modalButtons(flowState, setFlowState)}
    />
  );
}

function modalContent(flowState: ReauthenticateFlowState) {
  switch (flowState) {
    case ReauthenticateFlowState.START:
      return (
        <div>
          To successfully sync this account again, we need you to reauthenticate
          with your bank. This can be caused by changes to your bank's
          authentication process, password changes, or changes to your 2FA
          settings. Please begin the reauthentication process using the button
          below to fix this issue.
        </div>
      );
    case ReauthenticateFlowState.PLAID_LINK_OPEN:
      return <div className="h-20" />;
    case ReauthenticateFlowState.SYNCING:
      return (
        <div
          className={buildClasses(
            "flex",
            "flex-col",
            "items-center",
            "gap-6",
            "py-6",
          )}
        >
          <l-line-wobble size="300" speed="2" color="#7d57ff"></l-line-wobble>
          <div className={buildClasses("text-on-surface-500", "text-sm")}>
            Almost done, hang tight...
          </div>
        </div>
      );
    case ReauthenticateFlowState.END:
      return (
        <div
          className={buildClasses(
            "flex",
            "flex",
            "items-center",
            "gap-3",
            "mb-4",
            "max-w-md",
          )}
        >
          <RyeIcon
            name="check_circle"
            size="lg"
            className={buildClasses("text-green-500")}
          />
          <div className={buildClasses("text-center")}>
            Your account should now be syncing successfully.
          </div>
        </div>
      );
    default:
      return null;
  }
}

function modalButtons(
  flowState: ReauthenticateFlowState,
  setFlowState: (flowState: ReauthenticateFlowState) => void,
): ActionButton[] {
  switch (flowState) {
    case ReauthenticateFlowState.START:
    case ReauthenticateFlowState.PLAID_LINK_OPEN:
      return [
        {
          text: "Cancel",
          variant: "outlined",
          vibe: "subdued",
          action: () => {},
        },
        {
          text: "Reauthenticate",
          variant: "filled",
          vibe: "primary",
          action: () => {
            setFlowState(ReauthenticateFlowState.PLAID_LINK_OPEN);
          },
          close: false,
          disabled: flowState !== ReauthenticateFlowState.START,
        },
      ];
    case ReauthenticateFlowState.SYNCING:
    case ReauthenticateFlowState.END:
      return [
        {
          text: "Done",
          variant: "outlined",
          vibe: "subdued",
          disabled: flowState !== ReauthenticateFlowState.END,
          action: () => {},
        },
      ];
    default:
      return [];
  }
}
